import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import { GlobalFormMessage } from '@tn/formbuilder/utils/schema-types';
import { FormDataType } from '@tn/formbuilder/utils/schema-types';
import request, { RequestDocument } from 'graphql-request';
import { NextRouter } from 'next/router';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { flowRoutes, StateTypes } from '../../consts';
import { FlowEntity, InitMachineParams } from '../../types';
import { cleanFormData } from '../../utils/cleanFormData/cleanFormData';
import { getUserGuards, setBannerMessageCookie } from '../helpers';
import { ensureGetUser, getErrorMessageFromEvent } from '../helpers';
import { updateEmailMutation } from '../updateEmail/graphql/constants';

import {
    changePasswordMutation,
    editEmailCacheKey,
    editEmailQuery,
    editPasswordCacheKey,
    editPasswordQuery,
    resetForgotPasswordMutation,
    updateOptInCacheKey,
    updateOptInMutation,
} from './graphql/constants';
import {
    EDIT_EMAIL_LINK_SENT_MESSAGE,
    EMAIL_CHANGE_SUCCESS_BANNER_MESSAGE,
    MARKETING_COMMUNICATIONS_OPT_IN_MESSAGE,
    MARKETING_COMMUNICATIONS_OPT_OUT_MESSAGE,
    PASSWORD_RESET_SUCCESS_BANNER_MESSAGE,
    trackConfirmEmailEditSelect,
    trackEditEmailClick,
    trackEmailChangeSuccess,
    trackMarketingCheckboxToggle,
    trackMarketingSubscribeBannerDisplay,
    trackMarketingUnsubscribeBannerDisplay,
    trackPasswordResetSuccess,
} from './tracking';

const I18N_SOMETHING_WENT_WRONG = 'something_went_wrong_please_try_again';

/**
 * Navigates to the account page with optional banner message stored in a cookie.
 *
 * This is a reusable utility that any flow can use to navigate to the account page
 * and show a banner. The message is stored in a cookie, read in getServerSideProps, and shown
 * via the state machine (cookie cleared after read). For password-change success,
 * `trackPasswordResetSuccess` runs here after the cookie is set and before redirect — not on the account page.
 *
 * @param router - Next.js router instance
 * @param params - Optional banner message parameters
 * @param params.message - Translation key or message to display
 * @param params.variant - Banner variant (info, critical, success)
 * @param params.redirectToCheckEmail - If true, redirects to check-email instead of account (used after password change when backend clears auth cookies)
 *
 * @example
 * // Navigate with success message
 * navigateToAccountPage(router, {
 *   message: 'password_has_been_successfully_changed',
 *   variant: 'info'
 * });
 *
 * @example
 * // Navigate to check-email after password change (backend clears auth cookies)
 * navigateToAccountPage(router, {
 *   message: 'password_has_been_successfully_changed',
 *   variant: 'info',
 *   redirectToCheckEmail: true
 * });
 *
 * @example
 * // Navigate with error message
 * navigateToAccountPage(router, {
 *   message: 'failed_to_change_password',
 *   variant: 'critical'
 * });
 */
export const navigateToAccountPage = (
    router: NextRouter,
    params?: {
        message?: string;
        variant?: GlobalFormMessage['variant'];
        /** Email to show on account page when profile APIs lag after updateEmail */
        displayEmail?: string;
        /** If true, redirects to check-email instead of account (used after password change when backend clears auth cookies) */
        redirectToCheckEmail?: boolean;
    },
): void => {
    // Route based solely on explicit redirectToCheckEmail flag.
    // Callers must decide whether to redirect; routing is not determined by message content.
    const route =
        params?.redirectToCheckEmail === true
            ? '/account/check-email/'
            : flowRoutes[StateTypes.account];

    // Set cookie with banner message if provided
    // Cookie will be read and cleared in getServerSideProps
    if (params?.message) {
        setBannerMessageCookie(
            route,
            params.message,
            params.variant || 'info',
            params.displayEmail,
        );

        // Track password reset / email change success when navigating with success message
        trackPasswordResetSuccess(params.message);
        trackEmailChangeSuccess(params.message);
    }

    // Navigate to account page with full page refresh
    // Using window.location.href ensures getServerSideProps runs and can read the cookie
    if (typeof window !== 'undefined') {
        window.location.href = route;
    } else {
        // Fallback for server-side (shouldn't happen, but safe)
        router.push(route);
    }
};

export type AccountFlowContext = {
    formState?: GlobalFormMessage;
    isLoading?: boolean;
    token?: string;
    optIn?: boolean;
    pageType?: string;
    formBuilderRef?: FormBuilderRef | null;
};

export const submitFormChangePasswordActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<
            { message: string; isReLoginSuccess?: boolean },
            AccountFlowContext & FormDataType
        >(async ({ input }) => {
            const cleanedFormData = cleanFormData(input);
            const graphqlEndpoint = `/api/tn/app-shell/graphql/`;

            // Determine user auth status using the centralized helper
            const userGuards = getUserGuards(queryClient);
            const isLoggedIn = userGuards.isLoggedIn();

            // Choose mutation based on auth state
            const mutation = isLoggedIn ? changePasswordMutation : resetForgotPasswordMutation;

            const response = (await request(graphqlEndpoint, mutation as RequestDocument, {
                token: (input as { token?: string })?.token || '',
                password: cleanedFormData.password,
                passwordConfirm: cleanedFormData.passwordConfirm,
                recaptchaToken: cleanedFormData.recaptchaToken,
            })) as {
                changePassword?: { success: boolean; isReLoginSuccess?: boolean };
                resetForgotPassword?: { success: boolean };
            };

            // Handle response based on which mutation was used
            const success = isLoggedIn
                ? response.changePassword?.success
                : response.resetForgotPassword?.success;

            const isReLoginSuccess = isLoggedIn
                ? (response.changePassword?.isReLoginSuccess ?? false)
                : false;

            // Return message and re-login status
            return {
                message: success
                    ? PASSWORD_RESET_SUCCESS_BANNER_MESSAGE
                    : 'failed_to_change_password',
                isReLoginSuccess,
            };
        }),

    inputActor: <
        Mapper<
            AccountFlowContext,
            AnyEventObject,
            Omit<AccountFlowContext, 'formBuilderRef'>,
            AnyEventObject
        >
    >(({ context: { formBuilderRef: _formBuilderRef, ...rest } }) => {
        return { ...rest };
    }),
};

export const submitFormUpdateEmailActor: FlowEntity = {
    initActor: ({ queryClient: _queryClient }) =>
        fromPromise<string, AccountFlowContext & FormDataType>(async ({ input }) => {
            const cleanedFormData = cleanFormData(input);
            const token = (input as { token?: string })?.token || '';
            const email =
                (cleanedFormData.new_email as string | undefined) ||
                (cleanedFormData.email as string | undefined) ||
                '';
            const confirmEmail =
                (cleanedFormData.confirm_email as string | undefined) ||
                (cleanedFormData.confirmEmail as string | undefined) ||
                '';

            if (!token || !email || !confirmEmail) {
                throw new Error(I18N_SOMETHING_WENT_WRONG);
            }

            const graphqlEndpoint = `/api/tn/app-shell/graphql/`;
            const response = (await request(
                graphqlEndpoint,
                updateEmailMutation as RequestDocument,
                {
                    formFields: {
                        email,
                        confirmEmail,
                        token,
                    },
                },
            )) as { updateEmail: { success: boolean; errors?: string | null } };

            if (!response.updateEmail.success) {
                throw new Error(response.updateEmail.errors || I18N_SOMETHING_WENT_WRONG);
            }

            return EMAIL_CHANGE_SUCCESS_BANNER_MESSAGE;
        }),

    inputActor: <
        Mapper<
            AccountFlowContext,
            AnyEventObject,
            Omit<AccountFlowContext, 'formBuilderRef'>,
            AnyEventObject
        >
    >(({ context: { formBuilderRef: _formBuilderRef, ...rest } }) => {
        return { ...rest };
    }),
};

/**
 * True when submit params include the same field keys {@link submitFormUpdateEmailActor}
 * reads after `cleanFormData`: primary `new_email` | `email`, confirmation
 * `confirm_email` | `confirmEmail`.
 */
function hasUpdateEmailFormFieldKeys(params: AccountFlowContext & FormDataType): boolean {
    const p = params as Record<string, unknown>;
    const hasPrimaryEmail = 'new_email' in p || 'email' in p;
    const hasConfirmEmail = 'confirm_email' in p || 'confirmEmail' in p;
    return hasPrimaryEmail && hasConfirmEmail;
}

export const editPasswordActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<string, AccountFlowContext>(async ({ input: _input }) => {
            const user = await ensureGetUser(queryClient);
            await queryClient.fetchQuery({
                queryKey: [editPasswordCacheKey],
                queryFn: () => {
                    const requestHeaders = {
                        'request-source': editPasswordCacheKey,
                    };

                    return request(
                        `/api/tn/app-shell/graphql/`,
                        editPasswordQuery,
                        { email: user.userFullProfile?.email || '' },
                        {
                            ...requestHeaders,
                        },
                    );
                },
                staleTime: 0,
                gcTime: 0,
            });

            return 'a_password_reset_email_has_been_sent_to_your_email_address';
        }),
};

export const editEmailActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<string, AccountFlowContext>(async ({ input: _input }) => {
            await queryClient.fetchQuery({
                queryKey: [editEmailCacheKey],
                queryFn: () => {
                    const requestHeaders = {
                        'request-source': editEmailCacheKey,
                    };

                    return request(
                        `/api/tn/app-shell/graphql/`,
                        editEmailQuery,
                        {},
                        {
                            ...requestHeaders,
                        },
                    );
                },
                staleTime: 0,
                gcTime: 0,
            });
            return EDIT_EMAIL_LINK_SENT_MESSAGE;
        }),
};

export const optInActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<string, AccountFlowContext>(async ({ input }) => {
            const optIn = input?.optIn ?? false;
            const graphqlEndpoint = `/api/tn/app-shell/graphql/`;
            const requestHeaders = {
                'request-source': updateOptInCacheKey,
            };

            const response = (await queryClient.fetchQuery({
                queryKey: [updateOptInCacheKey, optIn],
                queryFn: () =>
                    request(
                        graphqlEndpoint,
                        updateOptInMutation as RequestDocument,
                        { optIn },
                        { ...requestHeaders },
                    ),
                staleTime: 0,
                gcTime: 0,
            })) as { updateOptIn: { success?: boolean } };

            if (!response?.updateOptIn?.success) {
                throw new Error(I18N_SOMETHING_WENT_WRONG);
            }

            return optIn
                ? MARKETING_COMMUNICATIONS_OPT_IN_MESSAGE
                : MARKETING_COMMUNICATIONS_OPT_OUT_MESSAGE;
        }),

    inputActor: <
        Mapper<
            AccountFlowContext,
            AnyEventObject,
            Pick<AccountFlowContext, 'optIn'>,
            AnyEventObject
        >
    >(({ context: { optIn } }) => {
        return { optIn: optIn ?? false };
    }),
};

export const accountFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router } = rawParams as InitMachineParams;
        return setup({
            types: {
                context: {} as AccountFlowContext,
                events: {} as
                    | {
                          type: 'EDIT_EMAIL';
                      }
                    | { type: 'EDIT_PASSWORD' }
                    | { type: 'CLEAR_BANNER_STATE' }
                    | {
                          type: 'OPT_IN_TOGGLED';
                          params: { optIn: boolean };
                      }
                    | {
                          type: 'SET_BANNER_MESSAGE';
                          params: {
                              message: string;
                              variant: 'info' | 'critical' | 'success' | 'warning';
                          };
                      }
                    | {
                          type: 'UPDATE_BANNER_STATE';
                          params: {
                              message: string;
                              variant: 'info' | 'critical' | 'success' | 'warning';
                          };
                      }
                    | {
                          type: 'SUBMIT_CLICK';
                          params: AccountFlowContext & FormDataType;
                      }
                    | {
                          type: 'CONNECT_FORMBUILDER_REF';
                          params: FormBuilderRef;
                      }
                    | { type: 'SUBMITTING' }
                    | { type: 'UPDATING_EMAIL' }
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitFormChangePasswordActor.initActor>>,
                          'submitting'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitFormChangePasswordActor.initActor>>,
                          'submitting'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitFormUpdateEmailActor.initActor>>,
                          'updatingEmail'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitFormUpdateEmailActor.initActor>>,
                          'updatingEmail'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof editEmailActor.initActor>>,
                          'editEmail'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof editEmailActor.initActor>>,
                          'editEmail'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof editPasswordActor.initActor>>,
                          'editPassword'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof editPasswordActor.initActor>>,
                          'editPassword'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof optInActor.initActor>>,
                          'optInToggled'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof optInActor.initActor>>,
                          'optInToggled'
                      >,
            },
            guards: {},
            actions: {
                saveData: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, [
                        'xstate.done.actor.editEmail',
                        'xstate.done.actor.editPassword',
                        'xstate.done.actor.submitting',
                        'xstate.done.actor.updatingEmail',
                        'xstate.done.actor.optInToggled',
                    ]);
                    const message = event.output as string;
                    // updatingEmail throws on error, so done = always success
                    const variant =
                        event.type === 'xstate.done.actor.updatingEmail'
                            ? 'info'
                            : message === 'failed_to_change_password'
                              ? 'critical'
                              : message === I18N_SOMETHING_WENT_WRONG
                                ? 'critical'
                                : 'info';
                    enqueue.assign({
                        formState: {
                            message,
                            variant,
                        },
                        isLoading: false,
                    });
                    // Toggle loading off for form builder if available
                    context.formBuilderRef?.toggleLoading(false);
                }),
                handleError: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, [
                        'xstate.error.actor.editEmail',
                        'xstate.error.actor.editPassword',
                        'xstate.error.actor.optInToggled',
                    ]);

                    const errorMessage = getErrorMessageFromEvent(event);
                    // Check if it's a 500 error (likely email template missing for PTV)
                    const is500Error =
                        errorMessage.includes('500') || errorMessage.includes('Server Error');

                    // Use a more readable message for 500 errors (email not sent)
                    const displayMessage = is500Error ? I18N_SOMETHING_WENT_WRONG : errorMessage;

                    context.formBuilderRef?.toggleLoading(false);
                    enqueue.assign({
                        formState: {
                            message: displayMessage,
                            variant: 'critical',
                        },
                        isLoading: false,
                    });
                }),
                clearBannerState: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CLEAR_BANNER_STATE');
                    enqueue.assign({
                        formState: undefined,
                        isLoading: false,
                    });
                }),
                setBannerMessage: enqueueActions(({ enqueue, event }) => {
                    // Action to set banner message from URL params (set by any flow navigating to account page)
                    // This is called when the account page receives bannerMessage/bannerVariant props from getServerSideProps
                    assertEvent(event, 'SET_BANNER_MESSAGE');
                    const setBannerEvent = event as {
                        type: 'SET_BANNER_MESSAGE';
                        params: { message: string; variant: GlobalFormMessage['variant'] };
                    };
                    enqueue.assign({
                        formState: {
                            message: setBannerEvent.params.message,
                            variant: setBannerEvent.params.variant,
                        },
                    });
                }),
                updateBannerState: enqueueActions(({ enqueue, event }) => {
                    // Action to update banner state from server-side props (read from cookie in getServerSideProps)
                    // This is called when the account page receives bannerMessage/bannerVariant props
                    assertEvent(event, 'UPDATE_BANNER_STATE');
                    const updateBannerEvent = event as {
                        type: 'UPDATE_BANNER_STATE';
                        params: { message: string; variant: GlobalFormMessage['variant'] };
                    };
                    enqueue.assign({
                        formState: {
                            message: updateBannerEvent.params.message,
                            variant: updateBannerEvent.params.variant,
                        },
                    });
                }),
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    const submitEvent = event as {
                        type: 'SUBMIT_CLICK';
                        params: AccountFlowContext & FormDataType;
                    };

                    const hasToken = Boolean(submitEvent.params.token);

                    if (hasUpdateEmailFormFieldKeys(submitEvent.params) && hasToken) {
                        return;
                    }

                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.assign({ ...submitEvent.params });
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                submitUpdateEmailForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    const submitEvent = event as {
                        type: 'SUBMIT_CLICK';
                        params: AccountFlowContext & FormDataType;
                    };

                    const hasToken = Boolean(submitEvent.params.token);

                    if (!hasUpdateEmailFormFieldKeys(submitEvent.params) || !hasToken) {
                        return;
                    }

                    trackConfirmEmailEditSelect(submitEvent.params.pageType);

                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.assign({ ...submitEvent.params });
                    enqueue.raise({ type: 'UPDATING_EMAIL' });
                }),
                navigateToAccount: enqueueActions(({ enqueue: _enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.submitting');
                    // Navigate to account page with success/error message in URL params
                    // Any flow can use navigateToAccountPage() to pass banner messages to the account page
                    // The message will be read from query params in getServerSideProps and displayed via state machine
                    const output = event.output as { message: string; isReLoginSuccess?: boolean };
                    const message = output.message;
                    const variant = message === 'failed_to_change_password' ? 'critical' : 'info';
                    // After password change success:
                    // - If re-login succeeded (isReLoginSuccess=true), user is authenticated, redirect to /account
                    // - If re-login failed (isReLoginSuccess=false), backend cleared auth cookies, redirect to /check-email
                    const redirectToCheckEmail =
                        message === PASSWORD_RESET_SUCCESS_BANNER_MESSAGE &&
                        !(output.isReLoginSuccess === true);
                    navigateToAccountPage(router, { message, variant, redirectToCheckEmail });
                }),
                navigateToAccountAfterUpdateEmail: enqueueActions(
                    ({ enqueue: _enqueue, event, context }) => {
                        assertEvent(event, 'xstate.done.actor.updatingEmail');
                        const message = event.output as string;
                        const formContext = context as AccountFlowContext & FormDataType;
                        const fromNewEmail =
                            typeof formContext.new_email === 'string' &&
                            formContext.new_email.trim()
                                ? formContext.new_email.trim()
                                : undefined;
                        const fromEmail =
                            typeof formContext.email === 'string' && formContext.email.trim()
                                ? formContext.email.trim()
                                : undefined;
                        const displayEmail = fromNewEmail ?? fromEmail;
                        navigateToAccountPage(router, {
                            message,
                            variant: 'info',
                            ...(displayEmail ? { displayEmail } : {}),
                        });
                    },
                ),
                assignFormBuilderRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    const connectEvent = event as {
                        type: 'CONNECT_FORMBUILDER_REF';
                        params: FormBuilderRef;
                    };
                    enqueue.assign({ formBuilderRef: connectEvent.params });
                }),
                assignOptIn: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'OPT_IN_TOGGLED');
                    const optInEvent = event as {
                        type: 'OPT_IN_TOGGLED';
                        params: { optIn: boolean };
                    };
                    enqueue.assign({ optIn: optInEvent.params.optIn });
                }),
                handleChangePasswordError: enqueueActions(
                    ({ enqueue: _enqueue, event, context }) => {
                        assertEvent(event, 'xstate.error.actor.submitting');
                        context.formBuilderRef?.toggleLoading(false);
                        // Navigate to account page with error message in URL params
                        // Any flow can use navigateToAccountPage() to pass banner messages to the account page
                        // The message will be read from query params in getServerSideProps and displayed via state machine
                        const errorEvent = event as ErrorActorEvent<
                            OutputFrom<ReturnType<typeof submitFormChangePasswordActor.initActor>>,
                            'submitting'
                        >;
                        const errorMessage = getErrorMessageFromEvent(errorEvent);
                        navigateToAccountPage(router, {
                            message: errorMessage,
                            variant: 'critical',
                        });
                    },
                ),
                handleUpdateEmailError: enqueueActions(({ enqueue: _enqueue, event, context }) => {
                    assertEvent(event, 'xstate.error.actor.updatingEmail');
                    const errorEvent = event as ErrorActorEvent<
                        OutputFrom<ReturnType<typeof submitFormUpdateEmailActor.initActor>>,
                        'updatingEmail'
                    >;
                    context.formBuilderRef?.toggleLoading(false);
                    const errorMessage = getErrorMessageFromEvent(errorEvent);
                    // Show the error inline on the change-email form so the user can
                    // correct fixable issues (e.g. email already in use) without losing
                    // the form. Cancel button still routes back to /account.
                    context.formBuilderRef?.toggleBanner({
                        message: errorMessage,
                        variant: 'critical',
                    });
                }),
                trackEditEmail: enqueueActions(({ enqueue: _enqueue, event }) => {
                    assertEvent(event, 'EDIT_EMAIL');
                    trackEditEmailClick();
                }),
                trackOptInToggled: enqueueActions(({ enqueue: _enqueue, event }) => {
                    assertEvent(event, 'OPT_IN_TOGGLED');
                    const optInEvent = event as {
                        type: 'OPT_IN_TOGGLED';
                        params: { optIn: boolean };
                    };
                    trackMarketingCheckboxToggle(optInEvent.params.optIn);
                }),
                trackOptInBannerDisplay: enqueueActions(({ enqueue: _enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.optInToggled');
                    const message = event.output as string;
                    if (message === MARKETING_COMMUNICATIONS_OPT_IN_MESSAGE) {
                        trackMarketingSubscribeBannerDisplay();
                    } else if (message === MARKETING_COMMUNICATIONS_OPT_OUT_MESSAGE) {
                        trackMarketingUnsubscribeBannerDisplay();
                    }
                }),
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: {},
        });
    },
};
